import React from 'react';
import { Box } from '@mui/material';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import DoNotDisturbOnOutlined from '@mui/icons-material/DoNotDisturbOnOutlined';
import { ActionMenu } from '../../ui-kit';
import { ConfirmDialog } from '../../components/dialog';

/**
 * The ⋯ at the end of a row in a live queue (ROCCO's leads, Policy
 * Servicing's requests), with Discard in it: take the row out for good, after
 * one question. Built once so both queues say the same thing the same way.
 *
 * Discard is greyed out, with the reason under it, while the row is on a
 * call: ending a live call halfway would be abrupt for whoever is on the phone.
 */
export default function QueueRowMenu({
  what, onCall, confirmTitle, confirmText, onDiscard,
}: {
  /** What the row is, for the button's name: "lead", "request". */
  what: string;
  onCall: boolean;
  confirmTitle: string;
  confirmText: string;
  /** Rejects with the server's reason, shown in the dialog. */
  onDiscard: () => Promise<void>;
}) {
  const [asking, setAsking] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);

  const discard = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await onDiscard();
      setAsking(false);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : `Could not discard the ${what}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    // The row opens its story on a click; the menu and its dialog open in a
    // layer of their own, whose clicks would otherwise reach the row too.
    <Box onClick={(event) => event.stopPropagation()} sx={{ display: 'inline-flex' }}>
      <ActionMenu
        aria-label={`More actions for this ${what}`}
        icon={<MoreHorizRounded fontSize="small" />}
        items={[{
          key: 'discard',
          label: 'Discard',
          description: onCall ? 'Wait for the call to end' : undefined,
          icon: <DoNotDisturbOnOutlined fontSize="small" />,
          tone: 'danger',
          disabled: onCall,
          onSelect: () => { setProblem(null); setAsking(true); },
        }]}
      />
      <ConfirmDialog
        open={asking}
        onClose={() => { if (!busy) setAsking(false); }}
        title={confirmTitle}
        description={confirmText}
        body={problem ? <Box sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Box> : undefined}
        tone="destructive"
        confirmLabel="Discard"
        onConfirm={discard}
        busy={busy}
      />
    </Box>
  );
}
